import { z } from "zod";

/**
 * The lifecycle of a run, as the Runs board labels it. `pending` is the state before the control plane
 * confirms the run, and a row never shows success before it is confirmed. `unknown` is kept until the
 * outcome is reconciled, and is never treated as a failure or a success.
 */
export const runStates = [
  "pending",
  "running",
  "waiting",
  "succeeded",
  "failed",
  "cancelled",
  "unknown",
] as const;
export const runStateSchema = z.enum(runStates);
export type RunState = z.infer<typeof runStateSchema>;

/**
 * How certain a credit figure is. The four kinds are always labelled, and `unknown` is never counted
 * as zero or dropped from a total.
 */
export const creditCertainties = ["confirmed", "reserved", "estimated", "unknown"] as const;
export const creditCertaintySchema = z.enum(creditCertainties);
export type CreditCertainty = z.infer<typeof creditCertaintySchema>;

/**
 * The lifecycle of an approval. `pending` is waiting for a decision, and a decision records who
 * decided. The author of the change is never eligible to decide their own.
 */
export const approvalStates = ["pending", "approved", "declined", "expired"] as const;
export const approvalStateSchema = z.enum(approvalStates);
export type ApprovalState = z.infer<typeof approvalStateSchema>;

/**
 * The welcome grant, as the CreditsModel board states it: 2,000 credits, expiring 14 days out,
 * at most 3 per user. Above 3 projects in 24 hours the project is still created but the grant
 * is held for review, so it is not spendable. One credit is $0.01.
 */
export const WELCOME_GRANT_CREDITS = 2_000;
export const WELCOME_GRANT_EXPIRY_DAYS = 14;
export const WELCOME_GRANT_MAX_PER_USER = 3;
export const WELCOME_GRANT_VELOCITY_WINDOW_HOURS = 24;
export const WELCOME_GRANT_VELOCITY_MAX_PROJECTS = 3;
export const CREDIT_TO_PENCE = 1;

/**
 * The picker's attention states, as the CreditsModel board states them: a balance at or below
 * 1,000 credits reads Low, and a grant within 7 days of expiry warns. Both live here so the API
 * and the UI cannot drift.
 */
export const LOW_CREDIT_BALANCE = 1_000;
export const GRANT_EXPIRY_WARNING_DAYS = 7;
